import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { fetchHorizonsForSettings } from '../utils/accessibleHorizons';
import NameSlugFields from '../components/NameSlugFields';
import { slugifyName, getSlugConflictMessage } from '../utils/slugify';
import '../styles/YumlPage.css';

const YUML_DIRECTIVES = ['@theme striped', '@theme solid', '@theme plain', '@css inline', '@table striped', '@table solid', '@table plain', '@table hidden-header', '@row', '@col', '@code json', '@code text', '@end'];
const SAMPLE_DATA_DEFAULT = '{\n  "output": {\n    "title": "Sample Report",\n    "company": "Acme Corp",\n    "revenue": "$1.2M",\n    "summary": "Strong quarter."\n  },\n  "raw": {}\n}';

const DEFAULT_SOURCE = `@theme striped
@css inline

# {{output.title}}

@table striped
| Company | Revenue |
| {{output.company}} | {{output.revenue}} |

@row
@col
## Summary
{{output.summary}}
@col
@code json
{{output.metrics}}
@end
`;

function YumlPage() {
  const { user } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [horizons, setHorizons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const previewTimer = useRef(null);
  const cssInputRef = useRef(null);

  const [form, setForm] = useState({
    name: '',
    slug: '',
    slugManual: false,
    source: DEFAULT_SOURCE,
    css_text: '',
    default_theme: 'striped',
    is_private: true,
    sampleDataJson: SAMPLE_DATA_DEFAULT,
    _suffix: '',
  });

  const existingSlugs = useMemo(
    () => templates.filter((t) => t.id !== editing?.id).map((t) => t.slug),
    [templates, editing]
  );

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const { horizons: hz, error: hzErr } = await fetchHorizonsForSettings(getAuthHeaders, user);
      if (hzErr) {
        setError(hzErr);
        setLoading(false);
        return;
      }
      setHorizons(hz || []);
      const all = [];
      for (const h of hz || []) {
        const resp = await fetch(
          `${API_BASE_URL}/casc/v1/api/yuml/suffixes/${encodeURIComponent(h.suffix)}`,
          { headers }
        );
        const json = await resp.json();
        if (json.success && Array.isArray(json.data)) {
          all.push(...json.data.map((t) => ({ ...t, _suffix: h.suffix, _horizonName: h.description || h.suffix })));
        }
      }
      setTemplates(all);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      name: '',
      slug: '',
      slugManual: false,
      source: DEFAULT_SOURCE,
      css_text: '',
      default_theme: 'striped',
      is_private: true,
      sampleDataJson: SAMPLE_DATA_DEFAULT,
      _suffix: horizons[0]?.suffix || '',
    });
    setPreviewHtml('');
    setPreviewError(null);
    setEditorOpen(true);
  };

  const openEdit = (tpl) => {
    setEditing(tpl);
    setForm({
      name: tpl.name || '',
      slug: tpl.slug || '',
      slugManual: true,
      source: tpl.source || '',
      css_text: tpl.css_text || '',
      default_theme: tpl.default_theme || 'striped',
      is_private: tpl.is_private !== false,
      sampleDataJson: SAMPLE_DATA_DEFAULT,
      _suffix: tpl._suffix,
    });
    setPreviewHtml('');
    setPreviewError(null);
    setEditorOpen(true);
  };

  const runPreview = useCallback(async (suffix, id, source, css_text, sampleDataJson) => {
    if (!suffix) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      let sampleData = {};
      try { sampleData = JSON.parse(sampleDataJson || '{}'); } catch (_) { sampleData = {}; }
      const headers = await getAuthHeaders();
      const url = id
        ? `${API_BASE_URL}/casc/v1/api/yuml/suffixes/${encodeURIComponent(suffix)}/${encodeURIComponent(id)}/preview`
        : `${API_BASE_URL}/casc/v1/api/yuml/suffixes/${encodeURIComponent(suffix)}/preview`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ source, css_text, sampleData }),
      });
      let json = {};
      try {
        json = await resp.json();
      } catch (_) {
        json = {};
      }
      if (json.success && typeof json.data?.html === 'string') {
        setPreviewHtml(json.data.html);
        setPreviewError(null);
      } else {
        setPreviewHtml('');
        const msg = json.error || json.message || (!resp.ok ? `Preview failed (HTTP ${resp.status})` : 'Preview returned no HTML');
        setPreviewError(msg);
      }
    } catch (e) {
      setPreviewHtml('');
      setPreviewError(e.message || 'Preview request failed');
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!editorOpen) return undefined;
    clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => {
      runPreview(form._suffix, editing?.id, form.source, form.css_text, form.sampleDataJson);
    }, 600);
    return () => clearTimeout(previewTimer.current);
  }, [editorOpen, form.source, form.css_text, form.sampleDataJson, form._suffix, editing, runPreview]);

  const handleSave = async () => {
    if (!form.name.trim() || !form.slug.trim() || !form._suffix) {
      setError('Name, slug, and horizon are required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim(),
        source: form.source,
        css_text: form.css_text,
        default_theme: form.default_theme,
        is_private: form.is_private,
      };
      const suffix = form._suffix;
      let resp;
      if (editing?.id) {
        resp = await fetch(
          `${API_BASE_URL}/casc/v1/api/yuml/suffixes/${encodeURIComponent(suffix)}/${encodeURIComponent(editing.id)}`,
          { method: 'PATCH', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }
        );
      } else {
        resp = await fetch(
          `${API_BASE_URL}/casc/v1/api/yuml/suffixes/${encodeURIComponent(suffix)}`,
          { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }
        );
      }
      const json = await resp.json();
      if (!json.success) throw new Error(json.error || 'Save failed');
      setEditorOpen(false);
      await loadTemplates();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Delete YUML template "${tpl.name}"?`)) return;
    try {
      const headers = await getAuthHeaders();
      await fetch(
        `${API_BASE_URL}/casc/v1/api/yuml/suffixes/${encodeURIComponent(tpl._suffix)}/${encodeURIComponent(tpl.id)}`,
        { method: 'DELETE', headers }
      );
      await loadTemplates();
    } catch (e) {
      setError(e.message);
    }
  };

  const downloadCssStub = async () => {
    if (!editing?.id || !form._suffix) return;
    const headers = await getAuthHeaders();
    const resp = await fetch(
      `${API_BASE_URL}/casc/v1/api/yuml/suffixes/${encodeURIComponent(form._suffix)}/${encodeURIComponent(editing.id)}/css-stub`,
      { headers }
    );
    const text = await resp.text();
    const blob = new Blob([text], { type: 'text/css' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${form.slug || 'yuml'}-stub.css`;
    a.click();
  };

  const handleCssUpload = async (file) => {
    if (!file) return;
    const text = await file.text();
    setForm((f) => ({ ...f, css_text: text }));
    if (editing?.id && form._suffix) {
      const headers = await getAuthHeaders();
      await fetch(
        `${API_BASE_URL}/casc/v1/api/yuml/suffixes/${encodeURIComponent(form._suffix)}/${encodeURIComponent(editing.id)}/css-upload`,
        { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ css_text: text }) }
      );
    }
  };

  const insertDirective = (text) => {
    setForm((f) => ({ ...f, source: `${f.source}${f.source.endsWith('\n') ? '' : '\n'}${text}\n` }));
  };

  return (
    <div className="yuml-page">
      <div className="yuml-header">
        <div>
          <h2>YUML — Your Useful Markup Language</h2>
          <p className="subtle">Define HTML output templates with tables, text, layout blocks, and custom CSS.</p>
        </div>
        <button type="button" className="btn primary" onClick={openCreate}>
          <i className="fas fa-plus" /> New template
        </button>
      </div>

      {error && <div className="error-text">{error}</div>}

      {loading ? (
        <div className="subtle"><i className="fas fa-spinner fa-pulse" /> Loading templates…</div>
      ) : (
        <table className="yuml-table-list">
          <thead>
            <tr><th>Name</th><th>Slug</th><th>Horizon</th><th>Theme</th><th /></tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <tr key={`${t._suffix}-${t.id}`}>
                <td>{t.name}</td>
                <td><code>{t.slug}</code></td>
                <td>{t._horizonName}</td>
                <td>{t.default_theme}</td>
                <td className="actions">
                  <button type="button" className="btn link" onClick={() => openEdit(t)}>Edit</button>
                  <button type="button" className="btn link danger" onClick={() => handleDelete(t)}>Delete</button>
                </td>
              </tr>
            ))}
            {!templates.length && (
              <tr><td colSpan={5} className="subtle">No YUML templates yet.</td></tr>
            )}
          </tbody>
        </table>
      )}

      {editorOpen && (
        <div className="yuml-modal-overlay">
          <div className="yuml-modal">
            <h3>{editing ? 'Edit template' : 'New template'}</h3>
            {!editing && (
              <div className="form-row">
                <label htmlFor="yuml-horizon">Horizon</label>
                <select
                  id="yuml-horizon"
                  value={form._suffix}
                  onChange={(e) => setForm((f) => ({ ...f, _suffix: e.target.value }))}
                >
                  {horizons.map((h) => (
                    <option key={h.suffix} value={h.suffix}>{h.description || h.suffix}</option>
                  ))}
                </select>
              </div>
            )}
            <NameSlugFields
              name={form.name}
              slug={form.slug}
              isEdit={!!editing}
              slugConflict={getSlugConflictMessage(form.slug, existingSlugs, !!editing)}
              onNameChange={(name) => setForm((f) => ({
                ...f,
                name,
                slug: f.slugManual ? f.slug : slugifyName(name),
              }))}
              onSlugChange={(slug) => setForm((f) => ({ ...f, slug, slugManual: true }))}
              nameLabel="Template name"
              slugLabel="Slug"
            />
            <div className="form-row">
              <label htmlFor="yuml-theme">Default theme</label>
              <select
                id="yuml-theme"
                value={form.default_theme}
                onChange={(e) => setForm((f) => ({ ...f, default_theme: e.target.value }))}
              >
                <option value="striped">striped</option>
                <option value="solid">solid</option>
                <option value="plain">plain</option>
              </select>
            </div>
            <div className="yuml-directives">
              {YUML_DIRECTIVES.map((d) => (
                <button key={d} type="button" className="btn chip" onClick={() => insertDirective(d)}>{d}</button>
              ))}
            </div>
            <div className="yuml-editor-split">
              <div className="yuml-editor-pane">
                <label htmlFor="yuml-source">YUML source</label>
                <textarea
                  id="yuml-source"
                  rows={14}
                  value={form.source}
                  onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}
                  spellCheck={false}
                />
                <label htmlFor="yuml-css">CSS</label>
                <textarea
                  id="yuml-css"
                  rows={6}
                  value={form.css_text}
                  onChange={(e) => setForm((f) => ({ ...f, css_text: e.target.value }))}
                  spellCheck={false}
                />
                <div className="yuml-css-actions">
                  <input ref={cssInputRef} type="file" accept=".css,text/css" style={{ display: 'none' }} onChange={(e) => handleCssUpload(e.target.files?.[0])} />
                  <button type="button" className="btn" onClick={() => cssInputRef.current?.click()}>Upload CSS</button>
                  {editing?.id && (
                    <button type="button" className="btn" onClick={downloadCssStub}>Download CSS stub</button>
                  )}
                </div>
                <label htmlFor="yuml-sample">Preview sample JSON</label>
                <textarea
                  id="yuml-sample"
                  rows={5}
                  value={form.sampleDataJson}
                  onChange={(e) => setForm((f) => ({ ...f, sampleDataJson: e.target.value }))}
                  spellCheck={false}
                />
                <p className="yuml-sample-hint">Scratch pad for preview only; not saved with the template.</p>
              </div>
              <div className="yuml-preview-pane">
                <div className="yuml-preview-label">
                  Preview {previewLoading && <i className="fas fa-spinner fa-pulse" />}
                </div>
                {previewError && (
                  <div className="yuml-preview-error" role="alert">
                    <i className="fas fa-exclamation-circle" /> {previewError}
                  </div>
                )}
                {form._suffix && (
                  <iframe
                    title="YUML preview"
                    className="yuml-preview-frame"
                    srcDoc={previewHtml}
                    sandbox="allow-same-origin"
                    style={{ background: '#fff', colorScheme: 'light' }}
                  />
                )}
              </div>
            </div>
            <div className="yuml-modal-actions">
              <button type="button" className="btn" onClick={() => setEditorOpen(false)}>Cancel</button>
              <button type="button" className="btn primary" disabled={saving} onClick={handleSave}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default YumlPage;
